// @flow
import React from 'react';

type Coords = {
  x: number,
  y: number
}

type Props = {
  cursorImg: Image;
  background: string;
  assetPath: string;
  againAudio: string;
  toggleStamps: Function;
}

type State = {
  mousePos: Coords,
  mouseVisible: boolean,
  userScale: number,
  backgroundRatioX: number,
  backgroundRatioY: number,
  scaleX: number,
  scaleY: number
}

export default class StampBoard extends React.PureComponent<Props, State> {
  ctx: CanvasRenderingContext2D;
  canvas: ?HTMLCanvasElement;
  backgroundImg: Image;
  imgAspect: number;
  canvasContainer: ?HTMLElement;
  audioClip: HTMLAudioElement;
  constructor(props: Props): void {
    super(props);
    this.state = {
      mousePos: { x: 0, y: 0 },
      mouseVisible: false,
      userScale: 1.0,
      scaleX: 1.0,
      scaleY: 1.0,
      backgroundRatioX: 1.0,
      backgroundRatioY: 1.0,
    };
  }

  componentDidMount(): void {
    if (this.props.againAudio) {
      this.audioClip = document.createElement('audio');
      this.audioClip.src = this.props.againAudio || '';
    }
    this.canvasContainer = document.getElementById('canvasContainer');
    this.canvasWrapper = document.getElementById('canvasWrapper');
    // A custom scalar to manually scale the stamp.
    this.backgroundImg = new Image();
    this.backgroundImg.src = this.props.background;
    this.backgroundImg.onload = () => {
      this.imgAspect = this.backgroundImg.width / this.backgroundImg.height;
      this.resizeCanvas();
      if (this.canvas) {
        const canvas = this.canvas;
        canvas.addEventListener('touchstart', (e: TouchEvent) => { this.drawStamp(e); }, { passive: false });
        this.ctx = canvas.getContext('2d');
        // Relation of the canvas style width and canvas actual width.
        // They are the same when starting, so scale is 1.
        window.addEventListener('resize', () => { this.resizeCanvas(); });
      }
    };
  }

  resizeCanvas = () => {
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const gameHeight = screenWidth > 1280 ? screenHeight - 130 : screenHeight;
    let windowWidth = 0;
    let windowHeight = 0;
    if (this.canvasContainer) {
      windowWidth = this.canvasContainer.clientWidth;
      windowHeight = windowWidth / this.imgAspect;
      if (windowHeight > gameHeight) {
        windowHeight = gameHeight;
        windowWidth = windowHeight * this.imgAspect;
      }
      this.canvasWrapper.style.height = `${windowHeight}px`;
      this.canvasWrapper.style.width = `${windowWidth}px`;
    }
    if (this.canvas) {
      // Amount of scaling done when resizing window. Starts as 1.0
      // Style width changes, but default width not, with that we know window resizing amount
      // to scale our cursor and stamp draw position.
      this.canvas.width = windowWidth;
      this.canvas.height = windowHeight;
      // this.setState({ scaleX: windowWidth / this.canvas.width });
      // this.setState({ scaleY: windowHeight / this.canvas.height });
      this.canvas.style.width = `${windowWidth}px`;
      this.canvas.style.height = `${windowHeight}px`;
      this.ctx = this.canvas.getContext('2d');
    }
    if (this.ctx) {
      // Ratio of the window vs the img size.
      // Handy to scale the cursor so it stays relative to the background image size.
      this.setState({ backgroundRatioX: windowWidth / this.backgroundImg.width });
      this.setState({ backgroundRatioY: windowHeight / this.backgroundImg.height });
      this.ctx.drawImage(this.backgroundImg, 0, 0, windowWidth, windowHeight);
    }
  }

  mouseOut = () => {
    this.setState({ mouseVisible: false });
  }

  mouseMove = (e: SyntheticMouseEvent<>) => {
    e.preventDefault();
    const mousePos = this.getMousePos(e);
    this.setState({ mousePos: { x: mousePos.x, y: mousePos.y }, mouseVisible: true });
  }

  clear = () => {
    if (this.canvas) {
      const { width, height } = this.canvas;
      this.ctx.clearRect(0, 0, width, height);
      this.ctx.drawImage(this.backgroundImg, 0, 0, width, height);
    }
  }

  drawStamp = (e: SyntheticMouseEvent<> | TouchEvent) => {
    e.preventDefault();
    const mousePos = this.getMousePos(e);
    const cursorImgOffX = this.props.cursorImg.width * this.state.backgroundRatioX * 0.5;
    const cursorImgOffY = this.props.cursorImg.height * this.state.backgroundRatioY * 0.5;
    const x = ((mousePos.x / this.state.scaleX) - cursorImgOffX);
    const y = ((mousePos.y / this.state.scaleY) - cursorImgOffY);
    const drawWidth = this.props.cursorImg.width * this.state.backgroundRatioX;
    const drawHeight = this.props.cursorImg.height * this.state.backgroundRatioY;
    this.ctx.drawImage(this.props.cursorImg, x, y, drawWidth, drawHeight);
    this.setState({ mouseVisible: false });
  }

  getMousePos = (e: any): Coords => {
    if (!this.canvas) { return { x: 0, y: 0 }; }
    const rect = this.canvas.getBoundingClientRect();
    const coords = { x: 0.0, y: 0.0 };
    if (e.clientX) {
      coords.x = e.clientX - rect.left;
      coords.y = e.clientY - rect.top;
    } else if (e.touches) {
      const touches = e.touches[0];
      coords.x = touches.clientX - rect.left;
      coords.y = touches.clientY - rect.top;
    }
    return coords;
  }

  playAudio = () => {
    if (!this.audioClip) return;
    this.audioClip.play();
  }

  render(): React$Element<any> {
    const cursorStyle = {
      width: this.props.cursorImg.width * this.state.backgroundRatioX * this.state.scaleX,
      height: this.props.cursorImg.height * this.state.backgroundRatioY * this.state.scaleY,
      left: `${this.state.mousePos.x - (this.props.cursorImg.width * this.state.backgroundRatioX * this.state.scaleX * 0.5)}px`,
      top: `${this.state.mousePos.y - (this.props.cursorImg.height * this.state.backgroundRatioY * this.state.scaleY * 0.5)}px`,
      display: this.state.mouseVisible ? 'block' : 'none',
    };

    const restartBtn = `${this.props.assetPath}assets/crafts/stamp/restartButton.png`;

    return (
      <React.Fragment>
        <div
          className="mobile-landscape:hidden iphonex-landscape:hidden w-12 h-12 xl:mt-36 absolute top-0 left-0 mt-3 ml-3 cursor-pointer flex items-center justify-center z-50"
          onClick={this.clear}
        >
          <img src={restartBtn} alt="begin opnieuw" className="w-full"/>
        </div>
        <div id="canvasContainer">
          <div id="canvasWrapper" className="relative overflow-hidden bg-white rounded-lg shadow mx-auto">
            <img style={cursorStyle} src={this.props.cursorImg.src} id="stampCursor" className="stamp__cursor" alt="" />
            <canvas
              className="rounded-lg p-2"
              onMouseMove={this.mouseMove}
              onMouseOut={this.mouseOut}
              onClick={this.drawStamp}
              ref={(c) => { this.canvas = c; }}
            />
          </div>
        </div>
      </React.Fragment>
    );
  }
}
